import React from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { useRefreshDashboard, useSelectedCase } from "./utils/hooks";
import { describeTransactionType } from "../../utils/textUtils";

import EnquiriesList from "./EnquiriesList";
import TodoSummary from "./TodoSummary";
import TodoList from "./TodoList";
import ListCompleteRequest from "./ListCompleteRequest";
import PageHeader from "../../components/PageHeader";
import ReferralCard from "../../components/ReferralCard";
import StatusUpdate from "./StatusUpdate";
import styled from "styled-components/macro";

import Card from "../../components/Card";
import DashboardCard from "../../components/DashboardCard";
import NotificationList from "./NotificationList";
import { formatMoneyNum } from "../../shared/utils/moneyUtils";

const ViewAll = styled(Link)`
  display: inline-block;
  color: var(--juno-color-link);
  margin-top: 1.5em;
  font-size: var(--juno-font-size-body-sm);
  border-bottom: 1px solid transparent;
  &:hover {
    cursor: pointer;
  }
  &:focus-visible {
    outline: var(--juno-focus-outline-width) solid var(--juno-color-focus);
    outline-offset: var(--juno-focus-outline-offset);
  }
`;

const CaseDetails = () => {
  const session = useSelector((state) => state.dashboardState.session);
  const selectedCase = useSelectedCase({ caseScreen: "case" });
  useRefreshDashboard({ staleTime: 0 });

  if (!selectedCase) {
    return (
      <>
        <PageHeader>Hello {session.first_name}</PageHeader>

        <Card title="Case not found" removeTopPadding>
          <p>We couldn't find that case.</p>
          <p>
            If you can’t find the case you’re looking for, please get in touch. Email us at{" "}
            <a href="mailto:hello@juno.legal">hello@juno.legal</a> along with the case reference or property address.
          </p>
          <p>
            <Link to="/dashboard">Back to your cases</Link>
          </p>
        </Card>
      </>
    );
  }

  const recentCompleteRequests = selectedCase.doables
    .filter((d) => d.status === "complete" && d.doable_type !== "enquiry")
    .slice(-5);

  let address = "";
  if (!!selectedCase.address) {
    address = "of " + selectedCase.address;
  }

  const propertyPrice = selectedCase.case_data?.property_price;
  const formattedPropertyPrice = propertyPrice ? formatMoneyNum(propertyPrice) : "";

  const transactionType = describeTransactionType(selectedCase.case_type);
  const cardTitle = `Your ${formattedPropertyPrice} ${transactionType} ${address} (${selectedCase.case_reference})`;

  // The backend sends a code with no link when it is inactive or not the client's own
  const referral = session.referral_code?.referralUrl ? session.referral_code : null;

  return (
    <>
      <PageHeader>
        Hello {session.first_name}
        <br />
        {cardTitle}
      </PageHeader>

      <TodoSummary transactionType={transactionType} selectedCase={selectedCase} referral={referral} />

      <TodoList selectedCase={selectedCase} />

      {selectedCase.notifications?.length > 0 && <NotificationList selectedCase={selectedCase} />}

      <EnquiriesList selectedCase={selectedCase} />

      <StatusUpdate
        title={`Current status of your ${describeTransactionType(selectedCase.case_type)}`}
        selectedCase={selectedCase}
        progressUpdate={selectedCase.progress_update}
      />

      {recentCompleteRequests.length > 0 && (
        <DashboardCard title="Things you’ve done">
          <ListCompleteRequest completeRequests={recentCompleteRequests} />
          <ViewAll to={`/dashboard/${selectedCase.case_reference}/things-youve-done`}>
            View all things you’ve done
          </ViewAll>
        </DashboardCard>
      )}

      {!!referral && (
        <ReferralCard
          id="refer"
          referralUrl={referral.referralUrl}
          rewardAmount={referral.rewardAmount}
          discountAmount={referral.discountAmount}
          prompt={selectedCase.client_referral_prompt ?? "refer_friend"}
          transactionType={transactionType}
        />
      )}
    </>
  );
};

export default CaseDetails;
